import { Paper } from '@mui/material';
import { dayjs, fromNullToUndefined, isNotNullOrUndefined, withDefault } from '@weft/utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { VyRangePicker } from '../../../components/new-design/inputs/VyRangePicker';
import { withPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { VyTableToolbar } from '../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { usePaymentsOverviews } from '../../../hooks/requests/use-payments-overviews/use-payments-overviews';
import { useFromDayParam } from '../../../hooks/search-params/use-from-day-param';
import { usePageParam } from '../../../hooks/search-params/use-page-param';
import { usePageSizeParam } from '../../../hooks/search-params/use-page-size-param';
import { useToDayParam } from '../../../hooks/search-params/use-to-day-param';
import { useDebounce } from '../../../hooks/use-debounce';
import { useSearchWithSearchParams } from '../../../hooks/use-search-with-search-params';
import { PAYMENTS_PAGE_DEFAULT_PAGE_SIZE } from './payments-page-consts';
import { PaymentsOverviewTable } from './PaymentsOverviewTable';

const PaymentsPage: React.FC = () => {
  const [page] = usePageParam();
  const [pageSize] = usePageSizeParam(PAYMENTS_PAGE_DEFAULT_PAGE_SIZE);

  const weekAgoDay = dayjs().subtract(7, 'day').startOf('day');
  
  const [fromDay] = useFromDayParam(weekAgoDay);
  const [toDay] = useToDayParam();
  const [query, setQuery] = useSearchWithSearchParams();
  const debouncedQuery = useDebounce(query);

  const { data: payments, isFetching: isFetchingInvoicesOverviews } = usePaymentsOverviews({
    page,
    pageSize,
    search: fromNullToUndefined(debouncedQuery),
    startTime: fromDay!.toDate(),
    endTime: toDay!.toDate(),
  }, {
    enabled: isNotNullOrUndefined(fromDay) && isNotNullOrUndefined(toDay),
  });

  const paymentsOverviews = payments?.paymentsOverviews ?? [];
  
  return (
    <>
      <Helmet>
        <title>Payments</title>
      </Helmet>

      <Paper
        variant="outlined"
        sx={theme => ({
          borderRadius: '12px',
          boxShadow: theme.shadows[1],
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        })}
      >
        <VyTableToolbar padding="24px 16px">
          <VyTableToolbar.Queries sx={{
            alignItems: 'start',
          }}
          >
            <VyRangePicker size="medium"/>

            <VyTableToolbar.SearchInput
              defaultValue={withDefault(debouncedQuery, '')}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search here"
            />
          </VyTableToolbar.Queries>
        </VyTableToolbar>

        <PaymentsOverviewTable
          items={paymentsOverviews}
          itemsCount={withDefault(payments?.count, 0)}
          loadingItems={isFetchingInvoicesOverviews}
        />
      </Paper>
    </>
  );
};

const paymentsPageWithGlobalSearchParams = withGlobalSearchParams(withPageLayout(PaymentsPage, {
  path:[
    <NavLink to="/invoices">Invoices</NavLink>,
    'Payments',
  ],
  title:'Payments',
}));
export { paymentsPageWithGlobalSearchParams as PaymentsPage };